/**
 * CertMgmtTab - Certificate inventory grid for a single environment (PROD / STAGE).
 * Rendered as part of Resources -> Cert Mgmt, route /resources/certmgmt
 */
import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import moment from 'moment';

import { AppContext } from '../../../../../RoutesWithAuth';
import { UserPreferencesContext } from '../../../../common/UserPreferences';
import axios from '../../../../common/AxiosConfig';
import FloatingMessage from '../../../../common/FloatingMessage';

import CustomGrid from '../../customgrid';
import EditDialog from '../../customgrid/EditDialog';
import DeleteDialog from '../../admin/settings/DeleteDialog';
import { LoadingIndicator } from '../../reports/reconcilationreport/MuiStyled';

import { Tooltip } from '@mui/material';
import PermissionButton from '../../PermissionButton';
import { RESOURCE_PERMISSIONS, useRolePermissions } from '../../../../common/RolePermissions';
import { Edit, Delete, Add } from '@mui/icons-material';

const CERT_TYPES = ['FTP', 'mTLS', 'SAML', 'Other'];
const SYNTHETIC_DOWNSTREAM_OPTIONS = [
    { value: 'cert_system:ALL', label: 'All Systems', sortOrder: 0 },
    { value: 'cert_system:OTHERS', label: 'Others', sortOrder: 1 },
];
const DATE_DISPLAY_FORMAT = 'MMM Do, YYYY';
const EXPIRY_WARNING_DAYS = 30;
const LONG_TEXT_LIMIT = 2000;

const useStyles = makeStyles(() => ({
    wrapper: {
        height: 'calc(100% - 50px)',
        position: 'relative',
        '& .expiringSoon-row td': {
            color: '#ed6c02 !important',
            fontWeight: 'bold',
        },
        '& .isInactive-row td': {
            color: '#e91e63 !important',
        },
    },
    toolbar: {
        display: 'flex',
        gap: '8px',
        marginBottom: '8px',
        alignItems: 'center',
    },
}));

const getEditFields = (downstreams) => [
    { name: 'downstream', label: 'Downstream', type: 'select', options: downstreams, required: true },
    { name: 'cert_type', label: 'Cert Type', type: 'select', options: CERT_TYPES, required: true },
    { name: 'cert_id', label: 'Cert ID', type: 'text', required: true, maxLength: 300 },
    { name: 'expiry_date', label: 'Expiry Date', type: 'date', required: true },
    { name: 'notes', label: 'Notes', type: 'text', maxLength: LONG_TEXT_LIMIT },
    { name: 'runbook_location', label: 'Runbook Location', type: 'text', maxLength: LONG_TEXT_LIMIT },
    { name: 'renewal_poc', label: 'Renewal POC', type: 'text', maxLength: LONG_TEXT_LIMIT },
    { name: 'impact', label: 'Impact', type: 'text', maxLength: LONG_TEXT_LIMIT },
    { name: 'cert_owner', label: 'Cert Owner', type: 'text', maxLength: LONG_TEXT_LIMIT },
    { name: 'cert_location', label: 'Cert Location', type: 'text', maxLength: LONG_TEXT_LIMIT },
    { name: 'is_active', label: 'Is Active', type: 'select', options: ['Yes', 'No'], required: true },
];

const EMPTY_RECORD = {
    downstream: '', cert_type: '', cert_id: '', expiry_date: '', notes: '',
    runbook_location: '', renewal_poc: '', impact: '', cert_owner: '',
    cert_location: '', is_active: 'Yes',
};

const getErrorMessage = (err, fallback) => err.response?.data?.message
    || err.response?.data?.error
    || (typeof err.response?.data === 'string' ? err.response.data : null)
    || fallback;

const dedupeOptionsByValue = (options) => {
    const seen = new Set();
    return options.filter(({ value }) => {
        if (!value || seen.has(value)) return false;
        seen.add(value);
        return true;
    });
};

const normalizeDownstreamOptions = (options) => {
    if (!Array.isArray(options)) return [];

    return options
        .map((opt) => {
            if (opt && typeof opt === 'object') {
                const value = opt.value ?? opt.id ?? opt.downstream_id ?? opt.app_id ?? '';
                const label = opt.label ?? opt.display ?? opt.name ?? opt.downstream ?? String(value);
                return { value: String(value), label: String(label), source: opt.source, sortOrder: opt.sortOrder };
            }

            return { value: String(opt), label: String(opt) };
        })
        .filter((opt) => opt.value);
};

const normalizeRowForEdit = (row) => {
    if (!row) return row;

    if (row.cert_system) {
        return {
            ...row,
            downstream: `cert_system:${row.cert_system}`,
        };
    }

    if (row.downstream_id !== null && row.downstream_id !== undefined && row.downstream_id !== '') {
        return {
            ...row,
            downstream: String(row.downstream_id),
        };
    }

    return row;
};

const normalizeSystemValue = (value) => String(value ?? '').trim().toLocaleLowerCase();

const isAllSystemsRow = (row) => (
    normalizeSystemValue(row.cert_system) === 'all'
    || normalizeSystemValue(row.downstream) === 'all'
    || normalizeSystemValue(row.downstream) === 'all systems'
);

const isOthersSystemRow = (row) => (
    normalizeSystemValue(row.cert_system) === 'others'
    || normalizeSystemValue(row.downstream) === 'others'
);

const getRowDownstreamOptionValue = (row) => {
    if (isAllSystemsRow(row)) return 'cert_system:ALL';
    if (isOthersSystemRow(row)) return 'cert_system:OTHERS';

    if (row.cert_system !== null && row.cert_system !== undefined && row.cert_system !== '') {
        return `cert_system:${row.cert_system}`;
    }

    if (row.downstream_id !== null && row.downstream_id !== undefined && row.downstream_id !== '') {
        return String(row.downstream_id);
    }

    return row.downstream === null || row.downstream === undefined ? '' : String(row.downstream);
};

const matchesDownstreamFilter = (row, selectedOptions) => {
    if (!Array.isArray(selectedOptions) || selectedOptions.length === 0 || selectedOptions.includes('All')) {
        return true;
    }

    return selectedOptions.some((option) => {
        if (option === 'All Systems') return isAllSystemsRow(row);
        if (option === 'Others') return isOthersSystemRow(row);

        return normalizeSystemValue(row.downstream) === normalizeSystemValue(option);
    });
};

const buildSubmitPayload = (env, formValues) => {
    const payload = {
        environment: env,
        cert_type: formValues.cert_type,
        cert_id: formValues.cert_id.trim(),
        expiry_date: formValues.expiry_date,
        notes: formValues.notes || '',
        runbook_location: formValues.runbook_location || '',
        renewal_poc: formValues.renewal_poc || '',
        impact: formValues.impact || '',
        cert_owner: formValues.cert_owner || '',
        cert_location: formValues.cert_location || '',
        is_active: formValues.is_active === 'Yes',
    };

    const downstreamValue = String(formValues.downstream || '').trim();

    if (downstreamValue.startsWith('cert_system:')) {
        payload.cert_system = downstreamValue.split(':')[1];
    } else if (/^\d+$/.test(downstreamValue)) {
        payload.downstream_id = downstreamValue;
    } else if (downstreamValue) {
        payload.downstream = downstreamValue;
    }

    return payload;
};

function CertMgmtTab({ env }) {
    const classes = useStyles();
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const isAdmin = context.isAdmin.get;
    const { hasPermission } = useRolePermissions();
    const canAdd = hasPermission(RESOURCE_PERMISSIONS.ADD_CERT);
    const canEdit = hasPermission(RESOURCE_PERMISSIONS.EDIT_CERT);
    const canDelete = hasPermission(RESOURCE_PERMISSIONS.DELETE_CERT);
    const { getPreferences, savePreferences, loaded: prefsLoaded } = useContext(UserPreferencesContext);

    const screenKey = `CERT_MGMT_${env}`;

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSavingPreferences, setIsSavingPreferences] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [isNewRecord, setIsNewRecord] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState(null);
    const [defaultFilters, setDefaultFilters] = useState({});
    const [defaultSortModel, setDefaultSortModel] = useState([]);
    const [defaultHiddenColumns, setDefaultHiddenColumns] = useState({});
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('error');
    const [downstreamOptions, setDownstreamOptions] = useState([]);

    const notify = useCallback((text, type = 'error') => {
        setMessage(text);
        setMessageType(type);
    }, []);

    const fetchData = useCallback(() => {
        setIsLoading(true);
        axios.get('/cert-mgmt', {
            headers: { Authorization: authToken },
            params: { environment: env },
        }).then(res => {
            setIsLoading(false);
            setData(Array.isArray(res.data) ? res.data : []);
        }).catch(err => {
            setIsLoading(false);
            console.error('Failed to fetch certificates:', err);
        });
    }, [authToken, env]);

    const fetchDownstreamOptions = useCallback(() => {
        axios.get('/cert-mgmt', {
            headers: { Authorization: authToken },
            params: { options: true },
        }).then(res => {
            setDownstreamOptions(Array.isArray(res.data) ? res.data : []);
        }).catch(err => {
            setDownstreamOptions([]);
            console.error('Failed to fetch certificate downstream options:', err);
        });
    }, [authToken]);

    useEffect(() => {
        fetchData();
        fetchDownstreamOptions();
    }, [fetchData, fetchDownstreamOptions]);

    useEffect(() => {
        if (!prefsLoaded) return;
        const prefs = getPreferences(screenKey);
        if (prefs.filters && Object.keys(prefs.filters).length > 0) setDefaultFilters(prefs.filters);
        if (prefs.sort && prefs.sort.field) setDefaultSortModel([prefs.sort]);
        if (prefs.hiddenColumns && Object.keys(prefs.hiddenColumns).length > 0) setDefaultHiddenColumns(prefs.hiddenColumns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefsLoaded, screenKey]);

    const handleSaveDefaults = useCallback((gridState) => {
        setIsSavingPreferences(true);
        return savePreferences(screenKey, {
            filters: gridState.filters,
            sort: gridState.sortModel?.[0] || {},
            hiddenColumns: gridState.hiddenColumns,
        }).then(() => {
            notify('Filter preferences saved successfully', 'success');
        }).catch(() => {
            notify('Failed to save filter preferences', 'error');
        }).finally(() => {
            setIsSavingPreferences(false);
        });
    }, [savePreferences, screenKey, notify]);

    const downstreamSelectOptions = useMemo(() => {
        const normalizedBackendOptions = normalizeDownstreamOptions(downstreamOptions);

        const syntheticOptions = SYNTHETIC_DOWNSTREAM_OPTIONS.map((opt) => ({
            ...opt,
            source: 'cert_system',
        }));

        const combined = dedupeOptionsByValue([
            ...syntheticOptions,
            ...normalizedBackendOptions,
        ]);

        return combined.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }, [downstreamOptions]);

    const downstreamOptionLabels = useMemo(() => {
        const labelsByValue = new Map(downstreamSelectOptions.map(({ value, label }) => [value, label]));
        const labelsByName = new Map(downstreamSelectOptions.map(({ label, value }) => [label.toLocaleLowerCase(), value]));

        return { labelsByValue, labelsByName };
    }, [downstreamSelectOptions]);

    const downstreams = useMemo(
        () => downstreamSelectOptions
            .filter(({ source }) => source !== 'filter')
            .map(({ label }) => label)
            .filter(Boolean),
        [downstreamSelectOptions]
    );

    const rows = useMemo(() => data.map(row => {
        const expiry = moment(row.expiry_date, [moment.ISO_8601, 'YYYY-MM-DD']);
        const isValidExpiry = !!row.expiry_date && expiry.isValid();
        const daysTillExpiry = Number.isFinite(row.days_till_expiry)
            ? row.days_till_expiry
            : (isValidExpiry ? expiry.startOf('day').diff(moment().startOf('day'), 'days') : '');
        const downstreamOptionValue = getRowDownstreamOptionValue(row);
        const downstreamNameValue = downstreamOptionLabels.labelsByName.get(downstreamOptionValue.toLocaleLowerCase());
        const downstreamOption = downstreamOptionLabels.labelsByValue.get(downstreamOptionValue)
            || downstreamOptionLabels.labelsByValue.get(downstreamNameValue);

        return {
            ...row,
            id: row.id ?? `${row.downstream}--${row.cert_id}`,
            downstream: downstreamOption || row.downstream || '',
            expiry_date: isValidExpiry ? expiry.format('YYYY-MM-DD') : '',
            days_till_expiry: daysTillExpiry,
            is_active: row.is_active === false || row.is_active === 'No' ? 'No' : 'Yes',
        };
    }), [data, downstreamOptionLabels]);

    const handleAddClick = useCallback(() => {
        if (!canAdd) return;
        setEditingRow(EMPTY_RECORD);
        setIsNewRecord(true);
        setEditDialogOpen(true);
    }, [canAdd]);

    const handleEditClick = useCallback((row) => {
        if (!canEdit) return;
        setEditingRow(normalizeRowForEdit(row));
        setIsNewRecord(false);
        setEditDialogOpen(true);
    }, [canEdit]);

    const handleEditClose = useCallback(() => {
        setEditDialogOpen(false);
        setEditingRow(null);
    }, []);

    const handleEditSave = useCallback((formValues) => {
        if (isNewRecord ? !canAdd : !canEdit) return;
        const payload = buildSubmitPayload(env, formValues);

        const request = isNewRecord
            ? axios.post('/cert-mgmt', payload, { headers: { Authorization: authToken } })
            : axios.put(`/cert-mgmt/${encodeURIComponent(editingRow.id)}`, payload, { headers: { Authorization: authToken } });

        request.then(() => {
            fetchData();
            notify(isNewRecord ? 'Certificate added successfully' : 'Certificate updated successfully', 'success');
        }).catch(err => {
            notify(getErrorMessage(err, 'An error occurred. Please try again.'), 'error');
        }).finally(() => {
            setEditDialogOpen(false);
            setEditingRow(null);
        });
    }, [authToken, env, isNewRecord, editingRow, fetchData, notify, canAdd, canEdit]);

    const handleDeleteClick = useCallback((row) => {
        if (!canDelete) return;
        setDeleteRecord(row);
        setOpenDeleteDialog(true);
    }, [canDelete]);

    const handleDeleteDialogClose = useCallback(() => {
        setDeleteRecord(null);
        setOpenDeleteDialog(false);
    }, []);

    const handleDeleteDialogSubmit = useCallback(() => {
        if (!deleteRecord || !canDelete) return;
        axios.delete(`/cert-mgmt/${encodeURIComponent(deleteRecord.id)}`, {
            headers: { Authorization: authToken },
            params: { environment: env },
        }).then(() => {
            fetchData();
            notify('Certificate deleted successfully', 'success');
        }).catch(err => {
            notify(getErrorMessage(err, 'Failed to delete certificate.'), 'error');
        }).finally(() => {
            setDeleteRecord(null);
            setOpenDeleteDialog(false);
        });
    }, [authToken, env, deleteRecord, fetchData, notify, canDelete]);

    const columns = useMemo(() => {
        const cols = [
            {
                name: '#', mapping: 'rownum', width: 50, frozen: true, sortable: false, hideable: false,
                disableColumnMenu: true, align: 'center',
                renderCell: ({ rowIndex }) => rowIndex + 1
            },
            { name: 'Downstream', mapping: 'downstream', width: 180, frozen: true, sortable: true, type: 'singleSelect', valueOptions: downstreams, filterMatcher: matchesDownstreamFilter, replaceAllSelectionOnClick: true, displayTootip: ['downstream'] },
            { name: 'Cert Type', mapping: 'cert_type', width: 110, sortable: true, type: 'singleSelect', valueOptions: CERT_TYPES },
            { name: 'Cert ID', mapping: 'cert_id', width: 220, sortable: true, hideable: false, displayTootip: ['cert_id', 25] },
            {
                name: 'Expiry Date', mapping: 'expiry_date', width: 150, sortable: true, type: 'date',
                renderCell: ({ value }) => (value ? moment(value, 'YYYY-MM-DD').format(DATE_DISPLAY_FORMAT) : '--')
            },
            { name: 'Days Till Expiry', mapping: 'days_till_expiry', width: 130, sortable: true, type: 'number', align: 'center' },
            { name: 'Notes', mapping: 'notes', width: 180, sortable: true, displayTootip: ['notes', 20] },
            { name: 'Runbook Location', mapping: 'runbook_location', width: 180, sortable: true, displayTootip: ['runbook_location', 20] },
            { name: 'Renewal POC', mapping: 'renewal_poc', width: 160, sortable: true, displayTootip: ['renewal_poc', 20] },
            { name: 'Impact', mapping: 'impact', width: 180, sortable: true, displayTootip: ['impact', 20] },
            { name: 'Cert Owner', mapping: 'cert_owner', width: 160, sortable: true, displayTootip: ['cert_owner', 20] },
            { name: 'Cert Location', mapping: 'cert_location', width: 180, sortable: true, displayTootip: ['cert_location', 20] },
            { name: 'Is Active', mapping: 'is_active', width: 100, sortable: true, type: 'singleSelect', valueOptions: ['Yes', 'No'] },
            {
                name: 'Updated Time', mapping: 'updated_on', width: 200, sortable: true, type: 'date',
                renderCell: ({ value }) => (value ? `${moment.utc(value).format(`${DATE_DISPLAY_FORMAT} HH:mm:ss`)} (UTC)` : '--')
            },
            { name: 'Updated By', mapping: 'updated_by', width: 160, sortable: true, displayTootip: ['updated_by'] },
        ];

        if (isAdmin) {
            cols.push({
                name: 'Edit', mapping: 'actions_edit', width: 70, sortable: false, hideable: false, disableColumnMenu: true, align: 'center',
                renderCell: ({ row }) => (
                    <Tooltip title="Edit">
                        <PermissionButton iconOnly permission={RESOURCE_PERMISSIONS.EDIT_CERT} aria-label="Edit" size="small" data-testid="edit" onClick={(e) => { e.stopPropagation(); handleEditClick(row); }}>
                            <Edit fontSize="small" />
                        </PermissionButton>
                    </Tooltip>
                )
            });
            cols.push({
                name: 'Delete', mapping: 'actions_delete', width: 80, sortable: false, hideable: false, disableColumnMenu: true, align: 'center',
                renderCell: ({ row }) => (
                    <Tooltip title="Delete">
                        <PermissionButton iconOnly permission={RESOURCE_PERMISSIONS.DELETE_CERT} aria-label="Delete" size="small" data-testid="delete" onClick={(e) => { e.stopPropagation(); handleDeleteClick(row); }}>
                            <Delete fontSize="small" />
                        </PermissionButton>
                    </Tooltip>
                )
            });
        }

        return cols;
    }, [downstreams, isAdmin, handleEditClick, handleDeleteClick]);

    const editFields = useMemo(() => getEditFields(downstreamSelectOptions), [downstreamSelectOptions]);

    const getRowClassName = useCallback((params) => {
        const rowClasses = [];
        const days = params?.row?.days_till_expiry;
        if (days !== '' && days !== null && days !== undefined && Number(days) < EXPIRY_WARNING_DAYS) {
            rowClasses.push('expiringSoon-row');
        }
        if (params?.row?.is_active === 'No') rowClasses.push('isInactive-row');
        return rowClasses.join(' ');
    }, []);

    return (
        <div className={classes.wrapper}>
            {(isLoading || isSavingPreferences) && <LoadingIndicator />}
            <FloatingMessage open={message !== ''} message={message} severity={messageType} onClose={() => setMessage('')} />

            {isAdmin && (
                <div className={classes.toolbar}>
                    <PermissionButton permission={RESOURCE_PERMISSIONS.ADD_CERT} variant="outlined" size="small" startIcon={<Add />} onClick={handleAddClick}>
                        Add Cert
                    </PermissionButton>
                </div>
            )}

            <CustomGrid
                columns={columns}
                rows={rows}
                customHeight="calc(100vh - 400px)"
                enableCustomFiltering={true}
                showFilterButton={true}
                showColumnSelector={true}
                defaultFilters={defaultFilters}
                defaultSortModel={defaultSortModel}
                defaultHiddenColumns={defaultHiddenColumns}
                onSaveDefaults={handleSaveDefaults}
                getRowClassName={getRowClassName}
            />

            <EditDialog
                open={editDialogOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
                row={editingRow}
                fields={editFields}
                title={isNewRecord ? `Add Cert (${env})` : `Edit Cert (${env})`}
                columns={3}
            />

            <DeleteDialog
                openDeleteDialog={openDeleteDialog}
                title="Delete Cert Confirmation"
                handleDeleteDialogClose={handleDeleteDialogClose}
                handleDeleteDialogSubmit={handleDeleteDialogSubmit}
            >
                Are you sure you want to delete the following cert:
                <br />
                <div className="deleteDialogKey">
                    {deleteRecord?.cert_id}
                </div>
            </DeleteDialog>
        </div>
    );
}

export default CertMgmtTab;